import * as Sentry from '@sentry/browser'
import { useCallback, useRef } from 'react'

import { reportingContext, reportingFingerprint } from './privacy'

/**
 * Tracks an async operation and reports errors to Sentry with context
 *
 * @param operation - The async function to execute
 * @param context - Context about the operation for error reporting
 * @returns The result of the operation
 *
 * @example
 * const data = await trackAsync(
 *   () => fetchUserData(userId),
 *   {
 *     operation: 'fetchUserData',
 *     databaseId,
 *     component: 'UserProfile'
 *   }
 * )
 */
export async function trackAsync<T>(
  operation: () => Promise<T>,
  context: {
    operation: string
    description?: string
    [key: string]: unknown
  },
): Promise<T> {
  context = { operation: 'frontend.error', ...reportingContext(context) }

  // Add breadcrumb for operation start
  Sentry.addBreadcrumb({
    message: `Starting ${context.operation}`,
    category: 'async.operation',
    level: 'info',
    data: context,
  })

  try {
    const result = await operation()

    // Add success breadcrumb
    Sentry.addBreadcrumb({
      message: `Completed ${context.operation}`,
      category: 'async.operation',
      level: 'info',
      data: { ...context, success: true },
    })

    return result
  } catch (error) {
    // Add error breadcrumb
    Sentry.addBreadcrumb({
      message: `Failed ${context.operation}`,
      category: 'async.operation',
      level: 'error',
      data: { ...context, success: false },
    })

    captureWithContext(error, context)

    // Re-throw the error so calling code can handle it
    throw error
  }
}

/**
 * React hook that wraps an async callback with error tracking
 * Useful for event handlers and callbacks that perform async operations
 *
 * The callback itself should be memoized with useCallback if needed.
 *
 * @param callback - The async callback to wrap
 * @param context - Context about the operation for error reporting
 * @returns A wrapped callback that tracks errors
 *
 * @example
 * const handleSubmit = usePortalCallback(
 *   async (data: FormData) => {
 *     await api.submitForm(data)
 *   },
 *   { operation: 'submitForm', component: 'UserProfile' }
 * )
 */
export function usePortalCallback<TArgs extends unknown[], TReturn>(
  callback: (...args: TArgs) => Promise<TReturn>,
  context: {
    operation: string
    description?: string
    [key: string]: unknown
  },
): (...args: TArgs) => Promise<TReturn> {
  const contextRef = useRef(context)
  contextRef.current = context

  return useCallback(
    async (...args: TArgs) => {
      return trackAsync(() => callback(...args), contextRef.current)
    },
    [callback],
  )
}

/**
 * Higher-order function that wraps an async function with error tracking
 * Useful for wrapping API client methods or service functions
 *
 * @param fn - The async function to wrap
 * @param operationName - Name of the operation for error reporting
 * @returns A wrapped function that tracks errors
 *
 * @example
 * const trackedFetch = withErrorTracking(
 *   fetchData,
 *   'fetchData'
 * )
 */
export function withErrorTracking<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  operationName: string,
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs) => {
    return trackAsync(() => fn(...args), {
      operation: operationName,
    })
  }
}

/**
 * Shared capture path for error messages. Use a code-owned operation/component;
 * metadata is limited to databaseId, orgId, orgName, errorType, statusCode,
 * durationMs, retryCount and success. Existing appId/organizationId aliases work.
 * The outgoing SDK hook redacts message values and removes sensitive scope data;
 * callers retain their original error for UI handling and rethrowing.
 *
 * @param error - The error to capture
 * @param context - Additional context for the error
 *
 * @example
 * try {
 *   await someOperation()
 * } catch (error) {
 *   captureWithContext(error, {
 *     operation: 'someOperation',
 *     databaseId
 *   })
 * }
 */
export function captureWithContext(
  error: unknown,
  context: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'error',
): void {
  withReportingScope(context, level, (safeContext) => {
    Sentry.captureException(error, {
      fingerprint: reportingFingerprint(safeContext),
    })
  })
}

/** Code-owned operation labels only; never interpolate user data into the message. */
export function captureMessageWithContext(
  operation: string,
  context: Record<string, unknown> = {},
  level: Sentry.SeverityLevel = 'error',
): void {
  withReportingScope({ ...context, operation }, level, (safeContext) => {
    Sentry.captureMessage(String(safeContext.operation || 'frontend.error'), {
      fingerprint: reportingFingerprint(safeContext),
    })
  })
}

function withReportingScope(
  context: Record<string, unknown>,
  level: Sentry.SeverityLevel,
  capture: (context: ReturnType<typeof reportingContext>) => void,
): void {
  const safeContext = reportingContext(context)
  Sentry.withScope((scope) => {
    scope.setContext('error_context', safeContext)
    scope.setLevel(level)
    Object.entries(safeContext).forEach(([key, value]) =>
      scope.setTag(key, String(value)),
    )
    capture(safeContext)
  })
}
